/**
 * SendActionEditor — the `send` block of a `handler: "send"` action button, or
 * of a "Send message" follow-up action (the SAME definition: one send, used by
 * the button and by automatic sends).
 *
 * It names everything a template may reach: the starter templates shipped
 * with the app (`Metadata_Model/message_templates/<obj>/<id>.json`), the
 * channels, the reports / visuals / dashboards a template may include (with
 * their parameters filled ONLY from this record's columns), the masters a
 * template may look recipients up in, the default grouping, the limits and
 * what tenants may change. At run time the server re-reads this block by the
 * action's name; nothing in a template can reach past it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Trash2, X } from 'lucide-react'
import toast from 'react-hot-toast'
import {
  ATTACHABLE_KINDS, CHANNELS, CHANNEL_LABELS, FILED_MODES, FILED_VERSIONS, FILED_VERSION_LABELS,
  KIND_LABELS, MODES, MODE_LABELS, TemplateComposer, sendErrorInfo,
  type AttachableKind, type ChannelKey, type FiledVersion, type DeliveryMode, type GroupingMode, type MessageTemplate,
  type SendActionBinding, type SendAttachable, type SendFields,
} from '@twasta/page-runtime/messaging'
import { allSqlModels, columnFormKey, type TransactionModel } from '@twasta/page-runtime/txn'
import { listDeployableObjects } from '@/api/deploy'
import { loadTransactionObject } from '@/api/designers'
import { listStarterTemplates, saveStarterTemplate, deleteStarterTemplate } from '@/api/messaging'

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '7px 10px', background: 'var(--color-input-bg)',
  border: '1px solid var(--color-border-strong)', borderRadius: 6, color: 'var(--color-text)',
  fontSize: '0.9375rem', minHeight: 36, boxSizing: 'border-box',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', fontWeight: 600, margin: '10px 0 4px',
}
const hintStyle: React.CSSProperties = { fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }
const chipStyle = (on: boolean, dashed = false): React.CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, borderRadius: 999, padding: '0 10px', minHeight: 30,
  fontSize: '0.8125rem', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  border: `1px ${dashed ? 'dashed' : 'solid'} ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
  background: on ? 'var(--color-accent-weak)' : 'var(--color-input-bg)',
  color: on ? 'var(--color-accent)' : 'var(--color-text)',
})
const addBtn: React.CSSProperties = {
  background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-accent)',
  fontSize: '0.8125rem', display: 'inline-flex', alignItems: 'center', gap: 3, padding: 0,
}
const WHEN_OPS = ['=', '!=', '>', '>=', '<', '<=', 'in', 'empty', 'not_empty', 'contains']

type Obj = { obj_name: string; obj_type: string }

function modelOf(data: unknown): TransactionModel | null {
  const root = (data as { content?: unknown } | null)?.content as { transaction?: TransactionModel } | TransactionModel | null
  if (!root) return null
  return ('transaction' in (root as object) ? (root as { transaction?: TransactionModel }).transaction : root) as TransactionModel || null
}

function columnsOf(model: TransactionModel | null, formNo?: string) {
  if (!model) return []
  const forms = allSqlModels(model)
  const f = formNo ? forms.find((x) => x.formNo === formNo) : forms[0]
  return (f?.sm.columns || []).map((w) => w.column).filter(Boolean).map((c) => ({
    key: columnFormKey(c), db_name: (c.db_name || c.name || '').toLowerCase(),
    heading: (c as { heading?: string }).heading || c.name, type: String(c.col_type || '').toUpperCase(),
  }))
}

/** The composer's fields, built from the models (the designer has no
 *  run-time binding endpoint to ask). */
function useSendFields(project: string, objName: string, lookups: string[]): SendFields | null {
  const [own, setOwn] = useState<TransactionModel | null>(null)
  const [masters, setMasters] = useState<Record<string, TransactionModel | null>>({})
  useEffect(() => {
    if (!project || !objName) return undefined
    let cancelled = false
    loadTransactionObject(project, objName)
      .then((d) => { if (!cancelled) setOwn(modelOf(d)) })
      .catch((err) => { console.warn('[SendActionEditor] could not load', objName, err) })
    return () => { cancelled = true }
  }, [project, objName])
  const key = lookups.join('|')
  useEffect(() => {
    if (!project) return undefined
    let cancelled = false
    for (const lk of lookups) {
      if (lk in masters) continue
      loadTransactionObject(project, lk)
        .then((d) => { if (!cancelled) setMasters((m) => ({ ...m, [lk]: modelOf(d) })) })
        .catch(() => { if (!cancelled) setMasters((m) => ({ ...m, [lk]: null })) })
    }
    return () => { cancelled = true }
    // masters is read only to skip loaded ones
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, key])
  return useMemo(() => {
    if (!own) return null
    const forms = allSqlModels(own).slice(1).map(({ formNo, sm }) => ({
      form_no: formNo, title: sm.form_title || `Form ${formNo}`, columns: columnsOf(own, formNo),
    }))
    return {
      header: columnsOf(own),
      forms,
      lookups: lookups.map((lk) => ({ obj: lk, columns: columnsOf(masters[lk] || null) })),
      group: [{ key: 'COUNT', heading: 'Number of records' }],
      system: [
        { key: 'SENDER.name', heading: 'Sender name' }, { key: 'SENDER.email', heading: 'Sender email' },
        { key: 'ORG.name', heading: 'Organization' }, { key: 'SEND_DATE', heading: 'Send date' },
        { key: 'LINK', heading: 'Secure link to the files' },
      ],
    }
  }, [own, masters, lookups])
}

export default function SendActionEditor({
  binding, onChange, project, objName, headerColumns, mode = 'button', buttonNames = [],
}: {
  binding: SendActionBinding | undefined
  onChange: (b: SendActionBinding) => void
  project: string
  /** The transaction being designed (starter templates are stored under it). */
  objName: string
  headerColumns: string[]
  mode?: 'button' | 'followup'
  /** Follow-up: this transaction's buttons (for the condition hint). */
  buttonNames?: string[]
}) {
  const b: SendActionBinding = binding || {}
  const set = useCallback((p: Partial<SendActionBinding>) => onChange({ ...b, ...p }), [b, onChange])
  const tplKey: 'default_template' | 'template' = mode === 'button' ? 'default_template' : 'template'
  const channels = b.channels || [...CHANNELS]
  const lookups = useMemo(() => b.lookups || [], [b.lookups])

  const [objects, setObjects] = useState<Obj[]>([])
  useEffect(() => {
    if (!project) return undefined
    let cancelled = false
    listDeployableObjects(project)
      .then((list) => { if (!cancelled) setObjects(list.map((o) => ({ obj_name: o.obj_name, obj_type: o.obj_type }))) })
      .catch((err) => console.warn('[SendActionEditor] could not list project objects', err))
    return () => { cancelled = true }
  }, [project])

  const [starters, setStarters] = useState<MessageTemplate[]>([])
  const loadStarters = useCallback(() => {
    if (!project || !objName) return
    listStarterTemplates(project, objName)
      .then(setStarters)
      .catch((err) => console.warn('[SendActionEditor] could not list starter templates', err))
  }, [project, objName])
  useEffect(() => { loadStarters() }, [loadStarters])

  const fields = useSendFields(project, objName, lookups)
  const [composing, setComposing] = useState<MessageTemplate | null | undefined>(undefined)

  const tplId = (b[tplKey] || '') as string
  const shipped = b.templates || []

  const saveStarter = async (tpl: MessageTemplate) => {
    try {
      const saved = await saveStarterTemplate(project, objName, tpl)
      const id = saved?.id || tpl.id
      loadStarters()
      set({
        templates: shipped.includes(id) ? shipped : [...shipped, id],
        ...(tplId ? {} : { [tplKey]: id }),
      })
      toast.success(`Starter template “${tpl.name}” saved`)
      setComposing(undefined)
    } catch (e) {
      const d = sendErrorInfo(e, 'The template could not be saved.')
      throw Object.assign(new Error(d.message), { problems: d.problems })
    }
  }

  const removeStarter = async (id: string) => {
    try {
      await deleteStarterTemplate(project, objName, id)
      set({ templates: shipped.filter((x) => x !== id), ...(tplId === id ? { [tplKey]: '' } : {}) })
      loadStarters()
    } catch (e) {
      toast.error(sendErrorInfo(e, 'The template could not be removed.').message)
    }
  }

  const atts = b.attachables || []
  const setAtt = (i: number, p: Partial<SendAttachable>) =>
    set({ attachables: atts.map((a, j) => (j === i ? { ...a, ...p } : a)) })

  const groupBy = (b.group_by || [])[0] || ''
  const groupValue = b.grouping === 'single' ? '__single' : (b.grouping === 'per_record' || !groupBy) ? '__record' : groupBy

  const ofKind = (k: string) => objects.filter((o) => o.obj_type === k)

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div style={{ ...hintStyle, marginBottom: 4 }}>
        {mode === 'button'
          ? 'Clicking opens a preview with the default template; the user can send it, switch template, or compose their own.'
          : 'Sent automatically — queued with the save or the action, delivered after it commits.'}
      </div>

      <label style={labelStyle}>{mode === 'button' ? 'Default template' : 'Template'}</label>
      <select style={inputStyle} value={tplId} onChange={(e) => set({ [tplKey]: e.target.value })}>
        <option value="">— choose —</option>
        {[...new Set([...shipped, ...starters.map((s) => s.id), ...(tplId ? [tplId] : [])])].map((id) => {
          const s = starters.find((x) => x.id === id)
          return <option key={id} value={id}>{s?.name || id}{s ? '' : ' (no file yet)'}</option>
        })}
      </select>

      <label style={labelStyle}>Starter templates shipped with the app</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {starters.map((s) => {
          const on = shipped.includes(s.id)
          return (
            <span key={s.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
              <button type="button" style={chipStyle(on)} title={on ? 'Offered on this send' : 'Not offered — click to offer'}
                      onClick={() => set({ templates: on ? shipped.filter((x) => x !== s.id) : [...shipped, s.id] })}>
                {on ? '✓' : '+'} {s.name || s.id}
              </button>
              <button type="button" style={{ ...addBtn, color: 'var(--color-text-secondary)' }} title="Edit"
                      onClick={() => setComposing(s)}>edit</button>
              <button type="button" style={{ ...addBtn, color: 'var(--color-error)' }} title="Delete the starter file"
                      onClick={() => void removeStarter(s.id)}><Trash2 size={12} /></button>
            </span>
          )
        })}
        <button type="button" style={chipStyle(false, true)} disabled={!fields} onClick={() => setComposing(null)}>
          <Plus size={12} /> New starter template
        </button>
      </div>

      <label style={labelStyle}>Channels this send may use</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {CHANNELS.map((c) => {
          const on = channels.includes(c)
          return (
            <button key={c} type="button" style={chipStyle(on)} aria-pressed={on}
                    onClick={() => set({ channels: on ? channels.filter((x) => x !== c) : [...channels, c] })}>
              {on ? '✓' : '+'} {CHANNEL_LABELS[c]}
            </button>
          )
        })}
      </div>
      <div style={{ ...hintStyle, marginTop: 4 }}>At run time a channel shows only if the tenant has connected it in IOFlow.</div>

      <label style={labelStyle}>Group selected records by</label>
      <select style={inputStyle} value={groupValue} onChange={(e) => {
        const v = e.target.value
        if (v === '__single') set({ grouping: 'single' as GroupingMode, group_by: [] })
        else if (v === '__record') set({ grouping: 'per_record' as GroupingMode, group_by: [] })
        else set({ grouping: 'per_entity' as GroupingMode, group_by: [v] })
      }}>
        <option value="__record">Each record separately</option>
        <option value="__single">One digest for all</option>
        {headerColumns.map((c) => <option key={c} value={c}>{c}</option>)}
      </select>

      <label style={labelStyle}>Reports, visuals and dashboards a template may include</label>
      {atts.map((a, i) => (
        <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 6, padding: 8, marginBottom: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            <select style={{ ...inputStyle, width: 120, flex: '0 0 auto' }} value={a.kind}
                    onChange={(e) => setAtt(i, { kind: e.target.value as AttachableKind, obj: '', id: '' })}>
              {ATTACHABLE_KINDS.map((k) => <option key={k} value={k}>{KIND_LABELS[k]}</option>)}
            </select>
            <select style={inputStyle} value={a.obj}
                    onChange={(e) => setAtt(i, { obj: e.target.value, id: a.id && a.id !== a.obj ? a.id : e.target.value })}>
              <option value="">— {a.kind === 'FILED' ? 'the report filed' : 'choose'} —</option>
              {ofKind(a.kind === 'FILED' ? 'R' : a.kind).map((o) => <option key={o.obj_name} value={o.obj_name}>{o.obj_name}</option>)}
              {a.obj && !ofKind(a.kind === 'FILED' ? 'R' : a.kind).some((o) => o.obj_name === a.obj) && <option value={a.obj}>{a.obj}</option>}
            </select>
            <button type="button" style={{ ...addBtn, color: 'var(--color-error)' }} title="Remove"
                    onClick={() => set({ attachables: atts.filter((_, j) => j !== i) })}><Trash2 size={13} /></button>
          </div>
          {a.kind === 'FILED' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <select style={inputStyle} value={a.repository || ''} aria-label="Repository"
                      onChange={(e) => setAtt(i, { repository: e.target.value || undefined })}>
                <option value="">— repository it was filed in —</option>
                {ofKind('K').map((o) => <option key={o.obj_name} value={o.obj_name}>{o.obj_name}</option>)}
                {a.repository && !ofKind('K').some((o) => o.obj_name === a.repository)
                  && <option value={a.repository}>{a.repository}</option>}
              </select>
              <select style={{ ...inputStyle, width: 170, flex: '0 0 auto' }} value={a.version || 'current'}
                      aria-label="Which version" onChange={(e) => setAtt(i, { version: e.target.value as FiledVersion })}>
                {FILED_VERSIONS.map((v) => <option key={v} value={v}>{FILED_VERSION_LABELS[v]}</option>)}
              </select>
            </div>
          )}
          {a.kind === 'FILED' && (
            <div style={hintStyle}>
              The copy the repository holds for this record (filed by a wizard print step) — never a fresh render.
            </div>
          )}
          {a.kind !== 'FILED' && <div style={hintStyle}>Parameters (filled from this record's columns)</div>}
          {a.kind !== 'FILED' && Object.entries(a.params || {}).map(([p, v]) => (
            <div key={p} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input style={{ ...inputStyle, flex: 1 }} value={p} aria-label="Parameter name"
                     onChange={(e) => {
                       const next: Record<string, string> = {}
                       for (const [k, val] of Object.entries(a.params || {})) next[k === p ? e.target.value : k] = val
                       setAtt(i, { params: next })
                     }} />
              <span>←</span>
              <select style={{ ...inputStyle, flex: 1 }} value={String(v).replace(/[{}]/g, '')}
                      onChange={(e) => setAtt(i, { params: { ...a.params, [p]: e.target.value ? `{${e.target.value}}` : '' } })}>
                <option value="">— column —</option>
                {headerColumns.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <button type="button" style={{ ...addBtn, color: 'var(--color-error)' }} title="Remove parameter"
                      onClick={() => { const next = { ...a.params }; delete next[p]; setAtt(i, { params: next }) }}><X size={13} /></button>
            </div>
          ))}
          {a.kind !== 'FILED' && (
            <button type="button" style={addBtn}
                    onClick={() => setAtt(i, { params: { ...(a.params || {}), [`param_${Object.keys(a.params || {}).length + 1}`]: '' } })}>
              <Plus size={12} /> Parameter
            </button>
          )}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {(a.kind === 'FILED' ? [...FILED_MODES] : [...MODES]).map((m) => {
              const on = (a.modes || [...MODES]).includes(m)
              return (
                <label key={m} style={{ ...hintStyle, display: 'flex', gap: 4, alignItems: 'center', color: 'var(--color-text)' }}>
                  <input type="checkbox" checked={on} onChange={() => {
                    const cur = a.modes || [...MODES]
                    const next = (on ? cur.filter((x) => x !== m) : [...cur, m]) as DeliveryMode[]
                    setAtt(i, { modes: next.length ? next : ['pdf'] })
                  }} /> {MODE_LABELS[m]}
                </label>
              )
            })}
          </div>
        </div>
      ))}
      <button type="button" style={addBtn}
              onClick={() => set({ attachables: [...atts, { id: '', kind: 'R', obj: '', params: {}, modes: [...MODES] }] })}>
        <Plus size={12} /> Add report / visual / dashboard / filed document
      </button>

      <label style={labelStyle}>Masters a template may look recipients up in</label>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {lookups.map((l) => (
          <span key={l} style={chipStyle(true)}>
            {l}
            <button type="button" style={{ ...addBtn, color: 'inherit' }} aria-label={`Remove ${l}`}
                    onClick={() => set({ lookups: lookups.filter((x) => x !== l) })}><X size={12} /></button>
          </span>
        ))}
        <select style={{ ...inputStyle, width: 200 }} value="" aria-label="Add master"
                onChange={(e) => { if (e.target.value) set({ lookups: [...lookups, e.target.value] }) }}>
          <option value="">+ Add master</option>
          {ofKind('T').filter((o) => !lookups.includes(o.obj_name) && o.obj_name !== objName)
            .map((o) => <option key={o.obj_name} value={o.obj_name}>{o.obj_name}</option>)}
        </select>
      </div>

      {mode === 'button' ? (
        <>
          <label style={labelStyle}>When clicked</label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem' }}>
            <input type="radio" checked={!b.auto_send} onChange={() => set({ auto_send: false })} />
            Open the preview with the default template
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem', marginTop: 4 }}>
            <input type="radio" checked={!!b.auto_send} onChange={() => set({ auto_send: true })} />
            Send immediately with the default template
          </label>
        </>
      ) : (
        <>
          <label style={labelStyle}>Only when</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <select style={{ ...inputStyle, width: 160 }} aria-label="Condition column" value={b.when?.column || ''}
                    onChange={(e) => set({ when: e.target.value ? { column: e.target.value, op: b.when?.op || '=', value: b.when?.value ?? '' } : null })}>
              <option value="">Always</option>
              {headerColumns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            {b.when && (
              <>
                <select style={{ ...inputStyle, width: 100 }} aria-label="Operator" value={b.when.op}
                        onChange={(e) => set({ when: { ...b.when!, op: e.target.value } })}>
                  {WHEN_OPS.map((o) => <option key={o} value={o}>{o.replace('_', ' ')}</option>)}
                </select>
                {b.when.op !== 'empty' && b.when.op !== 'not_empty' && (
                  <input style={{ ...inputStyle, width: 150 }} aria-label="Value" value={String(b.when.value ?? '')}
                         onChange={(e) => set({ when: { ...b.when!, value: e.target.value } })} />
                )}
              </>
            )}
          </div>
          {buttonNames.length > 0 && <div style={{ ...hintStyle, marginTop: 4 }}>A failed send never undoes the save or the action.</div>}
        </>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 }}>
        <div>
          <label style={labelStyle}>Max records per send</label>
          <input style={inputStyle} type="number" min={1} value={b.limits?.max_records ?? 500}
                 onChange={(e) => set({ limits: { ...(b.limits || {}), max_records: Math.max(1, Number(e.target.value) || 1) } })} />
        </div>
        <div>
          <label style={labelStyle}>Max message size (MB)</label>
          <input style={inputStyle} type="number" min={1} value={b.limits?.max_message_mb ?? 15}
                 onChange={(e) => set({ limits: { ...(b.limits || {}), max_message_mb: Math.max(1, Number(e.target.value) || 1) } })} />
        </div>
      </div>

      <label style={labelStyle}>Tenants</label>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem' }}>
        <input type="checkbox" checked={b.tenant_customizable !== false} onChange={(e) => set({ tenant_customizable: e.target.checked })} />
        Functional admins may customize this send for their organization
      </label>
      {mode === 'button' && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: '0.875rem', marginTop: 4 }}>
          <input type="checkbox" checked={b.user_templates !== false} onChange={(e) => set({ user_templates: e.target.checked })} />
          Users may compose and save their own templates
        </label>
      )}

      {composing !== undefined && fields && createPortal(
        <div role="dialog" aria-modal="true" aria-label="Starter template" style={{
          position: 'fixed', inset: 0, zIndex: 9700, background: 'rgba(15,20,28,.55)', display: 'flex',
          justifyContent: 'center', padding: '24px 16px', overflow: 'auto',
        }}>
          <div style={{
            width: '100%', maxWidth: 1340, margin: 'auto 0', background: 'var(--color-bg)', color: 'var(--color-text)',
            borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid var(--color-border)',
          }}>
            <TemplateComposer
              objName={objName}
              binding={{ ...b, channels: channels as ChannelKey[], lookups }}
              fields={fields}
              initial={composing}
              canShare
              mode="design"
              onSave={(tpl) => saveStarter(tpl)}
              onCancel={() => setComposing(undefined)}
            />
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
